import React, { useState, useEffect } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import {
  Button, Modal, Select, MenuItem, FormControl,
} from '@mui/material';
import Box from '@mui/material/Box';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import emotionStyled from '@emotion/styled';
import moment from 'moment-timezone';
import DatePicker from '../../components/datePicker';
import ActivityBar from '../../components/activityBar';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

const Avatar = emotionStyled.figure`
  margin: 0;
  padding: 8px 6px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: var(--Blue-Blue-01, #213D69);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%; /* 17.5px */
  background: var(--Blue-Blue-07, #D8E6FC);
  text-align: center;
  ${(props) => props.src && `
    background-size: cover;
    background-image: url(${props.src});
  `}
`;

function ManualTimeModal({
  showAddTimeModal,
  setShowAddTimeModal,
  workspaceMember,
  selectedTimezone,
  selectedActivity,
  selectedDate,
  handleAddManualTime,
  selectedManualTime,
  projects,
}) {
  const [dateAddTime, setDateAddTime] = useState(dayjs(selectedDate).toDate());
  const [startTime, setStartTime] = useState(dayjs().startOf('hour').toDate());
  const [endTime, setEndTime] = useState(dayjs().startOf('hour').add(1, 'hour').toDate());
  const [reason, setReason] = useState('');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [reason, startTime, endTime, projectId]);

  // Reset state when modal opens
  useEffect(() => {
    if (showAddTimeModal) {
      setDateAddTime(dayjs(selectedDate).toDate());
      setStartTime(dayjs().startOf('hour').toDate());
      setEndTime(dayjs().startOf('hour').add(1, 'hour').toDate());
      setReason('');
      setProjectId('');
      setError('');
    }
  }, [showAddTimeModal, selectedDate]);

  // Convert local time to target timezone
  const convertToTimezone = (date, timezoneOffset) => {
    const localOffset = new Date().getTimezoneOffset() * 60000;
    const targetOffset = timezoneOffset * 60000;
    return new Date(date.getTime() + localOffset + targetOffset);
  };

  // Get timezone offset in minutes
  const getTimezoneOffset = () => {
    const timezoneName = selectedTimezone;
    const date = new Date();

    try {
      const formattedDate = date.toLocaleString('en-US', { timeZone: timezoneName });
      const diff = (date - new Date(formattedDate)) / 60000;
      return Math.round(diff);
    } catch (e) {
      console.error('Invalid timezone:', timezoneName);
      return 0;
    }
  };

  const handleAddTime = async () => {
    setError('');

    // Validate required fields
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    // Get timezone offset
    const timezoneOffset = getTimezoneOffset();

    // Create date strings in target timezone
    const dateStr = dayjs(convertToTimezone(dateAddTime, timezoneOffset)).format('YYYY-MM-DD');
    const startTimeStr = dayjs(startTime).format('HH:mm:ss');
    const endTimeStr = dayjs(endTime).format('HH:mm:ss');

    const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

    // Combine into datetime strings
    const localStartStr = `${dateStr} ${startTimeStr}`;
    const localEndStr = `${dateStr} ${endTimeStr}`;

    // Parse in selected timezone
    const localStart = moment.tz(localStartStr, dateTimeFormat, selectedTimezone);
    const localEnd = moment.tz(localEndStr, dateTimeFormat, selectedTimezone);

    // Convert to UTC
    const utcStart = localStart.clone().utc();
    const utcEnd = localEnd.clone().utc();

    // Validate time order
    if (!utcStart.isBefore(utcEnd)) {
      setError('Start time must be before end time');
      return;
    }

    // Check for overlaps (assuming activity.startDate is UTC string)
    if (selectedActivity) {
      const hasOverlap = selectedActivity.some((activity) => {
        const activityStart = moment.utc(activity.startDate, 'YYYY-MM-DD HH:mm:ss');
        const activityEnd = activityStart.clone().add(activity.secondsElapsed, 'seconds');

        return (
          utcStart.isBefore(activityEnd) && utcEnd.isAfter(activityStart)
        );
      });

      if (hasOverlap) {
        setError('Selected time overlaps with existing activity');
        return;
      }
    }

    const timeEntry = {
      memberId: workspaceMember?.id,
      startTime: utcStart.format('YYYY-MM-DD HH:mm:ss'),
      endTime: utcEnd.format('YYYY-MM-DD HH:mm:ss'),
      reason: reason.trim(),
      timezone: selectedTimezone,
      duration: utcEnd.diff(utcStart, 'seconds'),
      projectId: projectId || null,
    };

    const res = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.manualActivityEntry(workspaceMember.id),
      data: timeEntry,
    });

    if (res.success === false) {
      setError(res.data.error);
      return;
    }

    // Close modal
    setShowAddTimeModal(false);
    handleAddManualTime();
  };

  return (
    <Modal
      open={showAddTimeModal}
      onClose={() => setShowAddTimeModal(false)}
      className="flex-row align-c jstfy-c"
      data-testid="export-all-timesheets-modal"
    >
      <Box style={{
        width: 'auto',
        minWidth: '600px',
        padding: 32,
        background: '#fff',
        borderRadius: 8,
      }}
      >
        <h5 className="m-b16 text-600" data-testid="modal-title">Add time</h5>

        <div className="d-flex gap-2">
          <Avatar src={workspaceMember?.user?.profilePicture}>
            {!workspaceMember?.user?.profilePicture && (
              (workspaceMember?.user?.firstName && workspaceMember?.user?.lastName
                && `${workspaceMember?.user?.firstName?.slice(0, 1)}
                ${workspaceMember?.user?.lastName?.slice(0, 1)}`)
            )}
          </Avatar>
          <div className="d-flex align-items-center">
            {`${workspaceMember?.user?.firstName} ${workspaceMember?.user?.lastName}`}
          </div>
        </div>

        <div className="add-time-modal-label mt-3">
          Timezone (
          {selectedTimezone}
          )
        </div>

        <div className="mt-2 d-flex justify-content-between gap-3">
          <DatePicker
            value={dateAddTime}
            onChange={(_date) => setDateAddTime(_date)}
            data-testid="datepicker"
            disabled
          />

          <div className="d-flex gap-2 align-items-center">
            <div className="add-time-modal-label">From</div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label=""
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
                renderInput={(params) => (
                  <TextField
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{ width: 140 }}
                  />
                )}
              />
            </LocalizationProvider>

            <div className="add-time-modal-label">To</div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                ampm
                label=""
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
                renderInput={(params) => (
                  <TextField
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{ width: 140 }}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
        </div>

        <br />
        <br />

        <ActivityBar
          manualTime={selectedManualTime}
          snapshots={selectedActivity}
          curDate={moment(selectedDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')}
          timezone={selectedTimezone}
        />

        <br />

        <div className="add-time-modal-label mt-3">
          Project (Optional)
        </div>
        <div className="mt-2 w-100">
          <FormControl fullWidth size="small">
            <Select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '5px',
                backgroundColor: 'white',
              }}
            >
              <MenuItem value="">
                <em>No Project</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="add-time-modal-label mt-3">
          Reason
        </div>
        <div className="mt-2 w-100">
          <TextField
            required
            label=""
            name="reason"
            variant="outlined"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                backgroundColor: 'white',
              },
            }}
            size="small"
          />
        </div>

        {error && (
          <div style={{ color: 'red', marginTop: '16px', textAlign: 'center' }}>
            {error}
          </div>
        )}

        <div className="exportAllForm-btns" style={{ marginTop: 24 }}>
          <Button
            onClick={() => setShowAddTimeModal(false)}
            color="secondary"
            sx={{ marginRight: 2 }}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTime}
            data-testid="addTime"
          >
            Add time
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

ManualTimeModal.propTypes = {
  showAddTimeModal: PropTypes.bool.isRequired,
  setShowAddTimeModal: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  workspaceMember: PropTypes.object.isRequired,
  selectedTimezone: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedActivity: PropTypes.array,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  handleAddManualTime: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedManualTime: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  projects: PropTypes.array,
};

ManualTimeModal.defaultProps = {
  selectedActivity: [],
  selectedManualTime: [],
  projects: [],
};

export default ManualTimeModal;
